/**
 * Kleine, rustige bouwstenen voor Medewerkers, HR-dashboard en Leveranciers:
 * een kerncijfer, een verdeling (horizontale balken in één kleur) en een foto met
 * initialen als terugval. Eén accentkleur; tekst in tekstkleuren.
 */
import React, { useState } from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';

export const INK = { tekst: '#2d3335', zacht: '#556071', stil: '#8a94a3', lijn: '#e5e8ec', vlak: '#f6f7f9' };
export const ACCENT = { color: '#526075', bg: '#d5e3fd' };

export async function haalJson(url, wat, opties) {
  const r = await authFetch(url, opties);
  return leesJson(r, wat);
}

export function Kerncijfer({ label, waarde, toelichting }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: INK.lijn, minWidth: 140, flex: '1 1 140px' }}>
      <Typography sx={{ fontSize: 12, color: INK.zacht, fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 800, color: INK.tekst, lineHeight: 1.2, fontFamily: '"Manrope", system-ui, sans-serif' }}>
        {waarde ?? '—'}
      </Typography>
      {toelichting && <Typography sx={{ fontSize: 12, color: INK.stil }}>{toelichting}</Typography>}
    </Paper>
  );
}

/** Verdeling als horizontale balken: label links, balk, aantal rechts. */
export function Verdeling({ titel, rijen, eenheid = '' }) {
  const max = Math.max(1, ...(rijen || []).map((r) => r.aantal));
  const totaal = (rijen || []).reduce((a, r) => a + r.aantal, 0);
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: INK.lijn }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK.tekst, mb: 1.5 }}>{titel}</Typography>
      {(!rijen || rijen.length === 0 || totaal === 0) ? (
        <Typography sx={{ fontSize: 13, color: INK.stil }}>Nog geen gegevens.</Typography>
      ) : rijen.map((r) => (
        <Tooltip key={r.label} placement="top-start"
          title={`${r.label}: ${r.aantal}${eenheid} (${Math.round((100 * r.aantal) / totaal)}%)`}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(90px, 38%) 1fr 36px', alignItems: 'center', gap: 1, py: 0.5 }}>
            <Typography noWrap sx={{ fontSize: 13, color: INK.zacht }}>{r.label}</Typography>
            <Box sx={{ height: 10, bgcolor: INK.vlak, borderRadius: 1 }}>
              <Box sx={{ height: 10, width: `${(100 * r.aantal) / max}%`, bgcolor: ACCENT.color, borderRadius: 1, minWidth: r.aantal ? 4 : 0 }} />
            </Box>
            <Typography sx={{ fontSize: 13, color: INK.tekst, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.aantal}</Typography>
          </Box>
        </Tooltip>
      ))}
    </Paper>
  );
}

const initialen = (naam) => (naam || '?').split(/\s+/).filter(Boolean).map((w) => w[0]).filter((c) => /[A-Za-zÀ-ÿ]/.test(c)).slice(0, 2).join('').toUpperCase() || '?';

/** Foto (Airtable-thumbnail; de URL verloopt, de server ververst hem) of initialen. */
export function Portret({ naam, foto, maat = 96 }) {
  const [kapot, setKapot] = useState(false);
  if (foto && !kapot) {
    return (
      <Box component="img" src={foto} alt="" loading="lazy" onError={() => setKapot(true)}
        sx={{ width: maat, height: maat, borderRadius: '50%', objectFit: 'cover', bgcolor: INK.vlak }} />
    );
  }
  return (
    <Box aria-hidden sx={{ width: maat, height: maat, borderRadius: '50%', bgcolor: INK.vlak, color: INK.zacht, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: maat / 3 }}>
      {initialen(naam)}
    </Box>
  );
}

export const bijgewerktTekst = (iso) => {
  if (!iso) return 'Nog niet gesynchroniseerd uit Airtable';
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return `Bijgewerkt uit Airtable: ${d.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`;
};
